import { createClient } from '@supabase/supabase-js'
import { config, validateEnvironment } from './config'

// Validate environment variables
const { isValid, missingVars } = validateEnvironment()

if (!isValid) {
  console.error('Missing required environment variables:', missingVars)
  // Don't throw here - let the app handle this gracefully with a banner
}

export const supabase = createClient(
  config.supabase.url || '',
  config.supabase.anonKey || '',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    },
    global: {
      headers: {
        'x-client-info': 'odonboard-web@1.0.0'
      }
    },
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
)

export const isSupabaseConfigured = isValid
export const missingSupabaseVars = missingVars

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          name: string | null
          full_name: string | null
          email: string
          role: 'owner' | 'admin' | 'member' | 'employee' | null
          global_role: 'superadmin' | null
          tenant_id: string | null
          /** Functional task-routing role. Orthogonal to the access-level `role`. */
          work_role: 'hr' | 'it' | 'manager' | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          name?: string | null
          full_name?: string | null
          email: string
          role?: 'owner' | 'admin' | 'member' | 'employee' | null
          global_role?: 'superadmin' | null
          tenant_id?: string | null
          work_role?: 'hr' | 'it' | 'manager' | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string | null
          full_name?: string | null
          email?: string
          role?: 'owner' | 'admin' | 'member' | 'employee' | null
          global_role?: 'superadmin' | null
          tenant_id?: string | null
          work_role?: 'hr' | 'it' | 'manager' | null
          created_at?: string
          updated_at?: string
        }
      }
      task_role_assignments: {
        Row: {
          id: string
          tenant_id: string
          employee_id: string
          work_role: 'hr' | 'it' | 'manager'
          assigned_user_id: string
          created_by: string | null
          created_at: string
        }
        Insert: {
          id?: string
          tenant_id: string
          employee_id: string
          work_role: 'hr' | 'it' | 'manager'
          assigned_user_id: string
          created_by?: string | null
          created_at?: string
        }
        Update: {
          work_role?: 'hr' | 'it' | 'manager'
          assigned_user_id?: string
          created_by?: string | null
        }
      }
      email_settings: {
        Row: {
          id: string
          admin_id: string
          from_address: string | null
          from_name: string | null
          reply_to: string | null
          daily_reminders_enabled: boolean
          reminder_time: string
          welcome_template: string | null
          invite_template: string | null
          reminder_template: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          admin_id: string
          from_address?: string | null
          from_name?: string | null
          reply_to?: string | null
          daily_reminders_enabled?: boolean
          reminder_time?: string
          welcome_template?: string | null
          invite_template?: string | null
          reminder_template?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          admin_id?: string
          from_address?: string | null
          from_name?: string | null
          reply_to?: string | null
          daily_reminders_enabled?: boolean
          reminder_time?: string
          welcome_template?: string | null
          invite_template?: string | null
          reminder_template?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      employee_files: {
        Row: {
          id: string
          employee_id: string
          file_name: string
          file_type: string
          file_size: number | null
          file_url: string
          category: string
          uploaded_by: string
          uploaded_at: string
          created_at: string
        }
        Insert: {
          id?: string
          employee_id: string
          file_name: string
          file_type: string
          file_size?: number | null
          file_url: string
          category?: string
          uploaded_by: string
          uploaded_at?: string
          created_at?: string
        }
        Update: {
          id?: string
          employee_id?: string
          file_name?: string
          file_type?: string
          file_size?: number | null
          file_url?: string
          category?: string
          uploaded_by?: string
          uploaded_at?: string
          created_at?: string
        }
      }
      employee_custom_fields: {
        Row: {
          id: string
          employee_id: string
          field_name: string
          field_value: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          employee_id: string
          field_name: string
          field_value?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          employee_id?: string
          field_name?: string
          field_value?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      employees: {
        Row: {
          id: string
          name: string
          email: string
          start_date: string
          position: string
          admin_id: string
          created_at: string
          updated_at: string
          manager_id: string | null
          location: string | null
          department: string | null
          status: 'active' | 'on_leave' | 'pending' | 'offboarded'
          employee_id_number: string | null
          onboarding_status: 'active' | 'completed'
          onboarding_completed_at: string | null
          portal_access_status: 'not_invited' | 'invited' | 'accepted' | 'expired'
          invitation_token: string | null
          invitation_expires_at: string | null
        }
        Insert: {
          id?: string
          name: string
          email: string
          start_date: string
          position: string
          admin_id: string
          created_at?: string
          updated_at?: string
          manager_id?: string | null
          location?: string | null
          department?: string | null
          status?: 'active' | 'on_leave' | 'pending' | 'offboarded'
          employee_id_number?: string | null
          onboarding_status?: 'active' | 'completed'
          updated_by?: string | null
          onboarding_completed_at?: string | null
          portal_access_status?: 'not_invited' | 'invited' | 'accepted' | 'expired'
          invitation_token?: string | null
          invitation_expires_at?: string | null
        }
        Update: {
          id?: string
          name?: string
          email?: string
          start_date?: string
          position?: string
          admin_id?: string
          created_at?: string
          updated_at?: string
          manager_id?: string | null
          location?: string | null
          department?: string | null
          status?: 'active' | 'on_leave' | 'pending' | 'offboarded'
          employee_id_number?: string | null
          onboarding_status?: 'active' | 'completed'
          updated_by?: string | null
          onboarding_completed_at?: string | null
          portal_access_status?: 'not_invited' | 'invited' | 'accepted' | 'expired'
          invitation_token?: string | null
          invitation_expires_at?: string | null
        }
      }
      templates: {
        Row: {
          id: string
          name: string
          description: string
          admin_id: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          description?: string
          admin_id: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          description?: string
          admin_id?: string
          created_at?: string
          updated_at?: string
        }
      }
      template_tasks: {
        Row: {
          id: string
          template_id: string
          title: string
          description: string
          relative_due_days: number
          assigned_to_role: string
          is_compliance: boolean
          policy_title: string | null
          policy_description: string | null
          policy_url: string | null
          requires_ack: boolean
          requires_upload: boolean
          expiry_interval_days: number | null
          created_at: string
        }
        Insert: {
          id?: string
          template_id: string
          title: string
          description?: string
          relative_due_days?: number
          assigned_to_role?: string
          is_compliance?: boolean
          policy_title?: string | null
          policy_description?: string | null
          policy_url?: string | null
          requires_ack?: boolean
          requires_upload?: boolean
          expiry_interval_days?: number | null
          created_at?: string
        }
        Update: {
          id?: string
          template_id?: string
          title?: string
          description?: string
          relative_due_days?: number
          assigned_to_role?: string
          is_compliance?: boolean
          policy_title?: string | null
          policy_description?: string | null
          policy_url?: string | null
          requires_ack?: boolean
          requires_upload?: boolean
          expiry_interval_days?: number | null
          created_at?: string
        }
      }
      tasks: {
        Row: {
          id: string
          title: string
          description: string
          employee_id: string
          assigned_to: string | null
          assigned_by: string | null
          completed_by: string | null
          due_date: string
          status: 'pending' | 'in_progress' | 'completed'
          completed_at: string | null
          task_type: 'onboarding' | 'compliance'
          is_compliance: boolean
          policy_title: string | null
          policy_description: string | null
          policy_url: string | null
          requires_ack: boolean
          requires_upload: boolean
          acknowledged_at: string | null
          acknowledged_by: string | null
          evidence_file_url: string | null
          approved_at: string | null
          approved_by: string | null
          rejected_at: string | null
          rejected_by: string | null
          expiry_interval_days: number | null
          expires_at: string | null
          priority: 'low' | 'medium' | 'high'
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          description?: string
          employee_id: string
          assigned_to?: string | null
          assigned_by?: string | null
          completed_by?: string | null
          due_date: string
          status?: 'pending' | 'in_progress' | 'completed'
          completed_at?: string | null
          task_type?: 'onboarding' | 'compliance'
          is_compliance?: boolean
          policy_title?: string | null
          policy_description?: string | null
          policy_url?: string | null
          requires_ack?: boolean
          requires_upload?: boolean
          acknowledged_at?: string | null
          acknowledged_by?: string | null
          evidence_file_url?: string | null
          approved_at?: string | null
          approved_by?: string | null
          rejected_at?: string | null
          rejected_by?: string | null
          expiry_interval_days?: number | null
          expires_at?: string | null
          priority?: 'low' | 'medium' | 'high'
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          description?: string
          employee_id?: string
          assigned_to?: string | null
          assigned_by?: string | null
          completed_by?: string | null
          due_date?: string
          status?: 'pending' | 'in_progress' | 'completed'
          completed_at?: string | null
          task_type?: 'onboarding' | 'compliance'
          is_compliance?: boolean
          policy_title?: string | null
          policy_description?: string | null
          policy_url?: string | null
          requires_ack?: boolean
          requires_upload?: boolean
          acknowledged_at?: string | null
          acknowledged_by?: string | null
          evidence_file_url?: string | null
          approved_at?: string | null
          approved_by?: string | null
          rejected_at?: string | null
          rejected_by?: string | null
          expiry_interval_days?: number | null
          expires_at?: string | null
          priority?: 'low' | 'medium' | 'high'
          created_at?: string
          updated_at?: string
        }
      }
      notifications: {
        Row: {
          id: string
          recipient_id: string
          sender_id: string | null
          type: 'task_assigned' | 'task_completed' | 'employee_added' | 'onboarding_completed'
          title: string
          message: string
          task_id: string | null
          employee_id: string | null
          read: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          recipient_id: string
          sender_id?: string | null
          type: 'task_assigned' | 'task_completed' | 'employee_added' | 'onboarding_completed'
          title: string
          message: string
          task_id?: string | null
          employee_id?: string | null
          read?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          recipient_id?: string
          sender_id?: string | null
          type?: 'task_assigned' | 'task_completed' | 'employee_added' | 'onboarding_completed'
          title?: string
          message?: string
          task_id?: string | null
          employee_id?: string | null
          read?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      employee_invitations: {
        Row: {
          id: string
          email: string
          name: string
          position: string
          start_date: string
          template_id: string | null
          admin_id: string
          status: 'pending' | 'accepted' | 'expired'
          invitation_token: string
          expires_at: string
          created_at: string
        }
        Insert: {
          id?: string
          email: string
          name: string
          position: string
          start_date: string
          template_id?: string | null
          admin_id: string
          status?: 'pending' | 'accepted' | 'expired'
          invitation_token?: string
          expires_at?: string
          created_at?: string
        }
        Update: {
          id?: string
          email?: string
          name?: string
          position?: string
          start_date?: string
          template_id?: string | null
          admin_id?: string
          status?: 'pending' | 'accepted' | 'expired'
          invitation_token?: string
          expires_at?: string
          created_at?: string
        }
      }
      subscriptions: {
        Row: {
          id: string
          admin_id: string
          stripe_customer_id: string | null
          stripe_subscription_id: string | null
          plan: 'free' | 'pro' | 'enterprise'
          status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'unpaid'
          seats_included: number
          seats_used: number
          billing_cycle: 'monthly' | 'yearly'
          amount: number
          currency: 'GBP' | 'EUR' | 'USD'
          current_period_start: string | null
          current_period_end: string | null
          cancel_at_period_end: boolean
          canceled_at: string | null
          trial_end: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          admin_id: string
          stripe_customer_id?: string | null
          stripe_subscription_id?: string | null
          plan?: 'free' | 'pro' | 'enterprise'
          status?: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'unpaid'
          seats_included?: number
          seats_used?: number
          billing_cycle?: 'monthly' | 'yearly'
          amount?: number
          currency?: 'GBP' | 'EUR' | 'USD'
          current_period_start?: string | null
          current_period_end?: string | null
          cancel_at_period_end?: boolean
          canceled_at?: string | null
          trial_end?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          admin_id?: string
          stripe_customer_id?: string | null
          stripe_subscription_id?: string | null
          plan?: 'free' | 'pro' | 'enterprise'
          status?: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'unpaid'
          seats_included?: number
          seats_used?: number
          billing_cycle?: 'monthly' | 'yearly'
          amount?: number
          currency?: 'GBP' | 'EUR' | 'USD'
          current_period_start?: string | null
          current_period_end?: string | null
          cancel_at_period_end?: boolean
          canceled_at?: string | null
          trial_end?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      invoices: {
        Row: {
          id: string
          admin_id: string
          stripe_invoice_id: string
          subscription_id: string | null
          amount: number
          currency: string
          status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void'
          invoice_pdf: string | null
          hosted_invoice_url: string | null
          invoice_date: string
          due_date: string | null
          paid_at: string | null
          created_at: string
        }
        Insert: {
          id?: string
          admin_id: string
          stripe_invoice_id: string
          subscription_id?: string | null
          amount: number
          currency?: string
          status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void'
          invoice_pdf?: string | null
          hosted_invoice_url?: string | null
          invoice_date: string
          due_date?: string | null
          paid_at?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          admin_id?: string
          stripe_invoice_id?: string
          subscription_id?: string | null
          amount?: number
          currency?: string
          status?: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void'
          invoice_pdf?: string | null
          hosted_invoice_url?: string | null
          invoice_date?: string
          due_date?: string | null
          paid_at?: string | null
          created_at?: string
        }
      }
      payment_methods: {
        Row: {
          id: string
          admin_id: string
          stripe_payment_method_id: string
          type: string
          card_brand: string | null
          card_last4: string | null
          card_exp_month: number | null
          card_exp_year: number | null
          is_default: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          admin_id: string
          stripe_payment_method_id: string
          type?: string
          card_brand?: string | null
          card_last4?: string | null
          card_exp_month?: number | null
          card_exp_year?: number | null
          is_default?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          admin_id?: string
          stripe_payment_method_id?: string
          type?: string
          card_brand?: string | null
          card_last4?: string | null
          card_exp_month?: number | null
          card_exp_year?: number | null
          is_default?: boolean
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}

export type EmployeeRow = Database['public']['Tables']['employees']['Row']